import { useMemo, useCallback } from "react";

import type { TConversation } from "@stores/chat-domain.types";
import { useAdvertiserStore } from "@stores/advertiser";
import { useChatStore } from "@stores/chat.store";
import { useChatConversations } from "@hooks/api/chat-websocket";

import {
  CHAT_CONVERSATION_TYPES,
  CHAT_MAX_NOTIFICATION_COUNTER,
  CHAT_USER_TAG,
  USER_TAG,
} from "@/utils/constants";
import { shortenName, getVehicleImageUrl, formatPrice, formatOdometer, capitalize } from "@/utils/helpers";

import type {
  TConversationItemConfig,
  TConversationItemLogicResult,
  TUserInfo,
  TVehicleData,
} from "./conversation-item.types";

/** Deriva dados de exibição (nome, badge, bloqueio) e handler de clique de um item de conversa. */
export const useConversationItemLogic = ({
  chatId,
  message,
  userNameFrom,
  userNameTo,
  userEmailFrom,
  userEmailTo,
  unreadMessages,
  userTag,
  detail,
}: Pick<
  TConversationItemConfig,
  | "chatId"
  | "message"
  | "userNameFrom"
  | "userNameTo"
  | "userEmailFrom"
  | "userEmailTo"
  | "unreadMessages"
  | "userTag"
  | "detail"
>) => {
  const conversationChatType = useChatStore((s) => s.conversation.chatType);
  const conversationActiveChatId = useChatStore((s) => s.conversation.active?.chatId);
  const storeSelectedTabIndex = useChatStore((s) => s.tabs.conversation);
  const { setConversationActive } = useChatConversations();

  const advertiserSource = useAdvertiserStore((s) => s.source);

  const unreadMessagesCounter =
    unreadMessages < CHAT_MAX_NOTIFICATION_COUNTER ? unreadMessages : "+99";

  const nameLastMessage = useMemo(() => {
    const isSenderToCurrentUser = message.senderId?.includes(
      String(advertiserSource?.Retorno?.Id ?? ""),
    );
    const nameByChatType =
      conversationChatType === CHAT_CONVERSATION_TYPES.sale
        ? userNameFrom
        : userNameTo;

    return isSenderToCurrentUser ? "Você" : nameByChatType;
  }, [
    message.senderId,
    advertiserSource,
    userNameFrom,
    userNameTo,
    conversationChatType,
  ]);

  const userInfo = useMemo((): TUserInfo => {
    const isSaleChat =
      conversationChatType === CHAT_CONVERSATION_TYPES.sale;

    if (isSaleChat) {
      return { email: userEmailFrom, name: userNameFrom };
    } else {
      return { email: userEmailTo, name: userNameTo };
    }
  }, [
    conversationChatType,
    userEmailFrom,
    userEmailTo,
    userNameFrom,
    userNameTo,
  ]);

  const findConversationByChatId = useCallback((): TConversation | undefined => {
    const state = useChatStore.getState();
    const source =
      state.conversation.chatType === "from"
        ? state.conversationFrom.source
        : state.conversationTo.source;
    return source.find((chat) => chat.chatId === chatId);
  }, [chatId]);

  const conversationClickHandler = useCallback(() => {
    const shouldRefreshUnread =
      (storeSelectedTabIndex === 0 ||
        (storeSelectedTabIndex === 1 && userTag !== CHAT_USER_TAG.Spam)) &&
      Boolean(unreadMessages);

    if (shouldRefreshUnread) {
      // Decremento otimista: a bolinha cai na hora. NÃO pedimos getUnreadCount na
      // leitura (vinha cedo demais, antes do back marcar como lida → defasado). O total
      // fresco é reconciliado quando o back sinaliza via statusMessage. ANCR-5329.
      useChatStore.getState().removeUnreadChats();
    }
    if (conversationActiveChatId === chatId) return false;
    const conversation = findConversationByChatId();
    return setConversationActive(conversation, chatId);
  }, [
    conversationActiveChatId,
    chatId,
    findConversationByChatId,
    storeSelectedTabIndex,
    unreadMessages,
    userTag,
    setConversationActive,
  ]);

  const isBlocked = userTag === USER_TAG.Blocked;
  const isActive = conversationActiveChatId === chatId;

  const vehicleData = useMemo((): TVehicleData => {
    const {
      city = "",
      state: uf = "",
      title = "",
      photoPath = "",
      price = "",
      odometer = "",
      year = "",
      vehicleType = "",
    } = detail ?? {};

    const formattedTitle = capitalize(title);
    const imageUrl = getVehicleImageUrl(photoPath, vehicleType);

    const infoParts = [
      formatPrice(price),
      year || "",
      formatOdometer(odometer),
      city && uf ? `${city}/${uf}` : "",
    ].filter(Boolean);

    const vehicleInfo = infoParts.join(" | ");
    const userName = shortenName(userInfo.name) ?? "";

    return { title: formattedTitle, imageUrl, photoPath, vehicleInfo, userName };
  }, [detail, userInfo.name]);

  return {
    nameLastMessage,
    unreadMessagesCounter,
    isBlocked,
    isActive,
    conversationClickHandler,
    userInfo,
    vehicleData,
  } satisfies TConversationItemLogicResult;
};
